import React, { useEffect, useState } from "react";
import { DeviceMobile, X } from "@phosphor-icons/react";

/**
 * Petite bannière d'installation PWA — s'affiche uniquement quand le navigateur
 * a émis `beforeinstallprompt` (Android/Chrome/Edge) et qu'on est sur un
 * appareil mobile. Sur iOS, on affiche une instruction textuelle ("partager → sur l'écran d'accueil").
 *
 * Dismiss-able : mémorise le refus 30 jours dans localStorage.
 */
const DISMISS_KEY = "mc68_install_dismiss_at";
const DISMISS_DAYS = 30;

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}
function isInStandalone() {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
}
function shouldShow() {
  try {
    const dismissed = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!dismissed) return true;
    const ageDays = (Date.now() - dismissed) / (1000 * 60 * 60 * 24);
    return ageDays > DISMISS_DAYS;
  } catch { return true; }
}

export default function InstallPWAButton() {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isInStandalone()) return; // déjà installé
    if (!shouldShow()) return;

    // Android/Chrome : beforeinstallprompt
    const onPrompt = (e) => {
      e.preventDefault();
      setPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // iOS : pas d'event, on affiche une hint après un petit délai
    if (isIos() && !isInStandalone()) {
      const t = setTimeout(() => { setIosHint(true); setVisible(true); }, 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    try {
      await prompt.userChoice;
    } catch { /* user cancelled */ }
    setPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="bg-primary text-primary-foreground rounded-2xl p-3 sm:p-4 shadow-md flex items-center gap-3"
      data-testid="pwa-install-banner"
      role="region"
      aria-label="Installer l'application"
    >
      <div className="shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
        <DeviceMobile size={20} weight="duotone"/>
      </div>
      <div className="flex-1 min-w-0">
        {iosHint ? (
          <>
            <div className="font-display font-semibold text-sm leading-tight">Installe MonCommerce68 sur ton téléphone</div>
            <div className="text-[11px] opacity-90 leading-tight mt-0.5">
              Tape sur <b>Partager</b> <span aria-hidden="true">⎋</span> puis <b>Sur l&apos;écran d&apos;accueil</b>. Tu ouvres ton portail comme une vraie appli.
            </div>
          </>
        ) : (
          <>
            <div className="font-display font-semibold text-sm leading-tight">Ajoute MonCommerce68 à ton écran d&apos;accueil</div>
            <div className="text-[11px] opacity-90 leading-tight mt-0.5">
              Un tap pour lancer ton portail — comme une vraie appli, en plein écran.
            </div>
          </>
        )}
      </div>
      {prompt && !iosHint && (
        <button
          type="button"
          onClick={install}
          className="shrink-0 bg-white text-primary hover:bg-white/90 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-xl"
          data-testid="pwa-install-btn"
        >
          Installer
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
        aria-label="Fermer"
        data-testid="pwa-install-close"
      >
        <X size={12} weight="bold"/>
      </button>
    </div>
  );
}
